import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import PhoneInput from "react-phone-input-2";
import { ChevronDown, ChevronUp } from "lucide-react";
import { clx } from "../../../utils/Helpers";
import { getPhoneInputLocalization } from "../../../utils/phoneInputLocalization";
import messages from "../../../assets/locale/messages";
import { UIFieldShell, useUIField } from "../form";
import "react-phone-input-2/lib/style.css";
import "./UIPhoneField.scss";

const digitsOnly = (value) => String(value ?? "").replace(/\D/g, "");

const toNationalDigits = (rawDigits, dialCode) => {
  const digits = digitsOnly(rawDigits);
  const dial = digitsOnly(dialCode);

  if (!digits) {
    return "";
  }

  if (dial && digits.startsWith(dial) && digits.length > dial.length) {
    return digits.slice(dial.length);
  }

  return digits;
};

const UIPhoneField = ({
  label,
  optionalLabel,
  required,
  name,
  value,
  error,
  helperText,
  onChange,
  onBlur,
  country,
  countryCode,
  placeholder,
  disabled,
  size,
  preferredCountries,
  formValueIsFullInternational,
  disableDropdown,
  ...props
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared: sharedMessages } = messages[lang];
  const phoneLocalization = useMemo(
    () => getPhoneInputLocalization(lang),
    [lang],
  );

  const field = useUIField({
    name,
    value,
    error,
    helperText,
  });

  const [selectedCountry, setSelectedCountry] = useState(
    String(country || "sa").toLowerCase()
  );
  const [selectedDialCode, setSelectedDialCode] = useState(
    digitsOnly(countryCode || "966")
  );

  useEffect(() => {
    if (country) {
      setSelectedCountry(String(country).toLowerCase());
    }
  }, [country]);

  useEffect(() => {
    setSelectedDialCode(digitsOnly(countryCode || "966"));
  }, [countryCode]);

  const fieldDigits = digitsOnly(field.value);
  const nationalDigits = useMemo(
    () =>
      formValueIsFullInternational
        ? fieldDigits
        : toNationalDigits(fieldDigits, selectedDialCode),
    [fieldDigits, formValueIsFullInternational, selectedDialCode],
  );

  const phoneValue = useMemo(() => {
    if (formValueIsFullInternational) {
      return fieldDigits;
    }

    if (!nationalDigits) {
      return selectedDialCode || "";
    }

    return `${selectedDialCode}${nationalDigits}`;
  }, [
    fieldDigits,
    formValueIsFullInternational,
    nationalDigits,
    selectedDialCode,
  ]);

  const isInternalChangeRef = useRef(false);
  const hasMountedRef = useRef(false);
  const directionRef = useRef(null);
  const [phoneInputKey, setPhoneInputKey] = useState(0);

  const syncDropdownPosition = useCallback(() => {
    const root = directionRef.current;
    if (!root || disableDropdown) {
      return;
    }

    const container = root.querySelector(".ui-phone-field__container");
    const list = root.querySelector(".ui-phone-field__dropdown.country-list");
    const isOpen = root.querySelector(".ui-phone-field__button.open");

    if (!list) {
      return;
    }

    list.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");

    if (!isOpen || !container) {
      list.style.removeProperty("position");
      list.style.removeProperty("top");
      list.style.removeProperty("left");
      list.style.removeProperty("width");
      list.style.removeProperty("min-width");
      list.style.removeProperty("max-width");
      list.style.removeProperty("z-index");
      return;
    }

    const rect = container.getBoundingClientRect();
    const width = `${rect.width}px`;
    list.style.setProperty("position", "fixed", "important");
    list.style.setProperty("top", `${rect.bottom + 4}px`, "important");
    list.style.setProperty("left", `${rect.left}px`, "important");
    list.style.setProperty("width", width, "important");
    list.style.setProperty("min-width", width, "important");
    list.style.setProperty("max-width", width, "important");
    list.style.setProperty("z-index", "1300", "important");
  }, [disableDropdown, lang]);

  const scheduleSyncDropdownPosition = useCallback(() => {
    syncDropdownPosition();
    window.requestAnimationFrame(syncDropdownPosition);
  }, [syncDropdownPosition]);

  useEffect(() => {
    const root = directionRef.current;
    if (!root || disableDropdown) {
      return undefined;
    }

    const observer = new MutationObserver(scheduleSyncDropdownPosition);
    observer.observe(root, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class"],
    });
    window.addEventListener("resize", scheduleSyncDropdownPosition);
    window.addEventListener("scroll", scheduleSyncDropdownPosition, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", scheduleSyncDropdownPosition);
      window.removeEventListener("scroll", scheduleSyncDropdownPosition, true);
    };
  }, [disableDropdown, phoneInputKey, scheduleSyncDropdownPosition]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      if (fieldDigits) {
        setPhoneInputKey((key) => key + 1);
      }
      return;
    }

    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    setPhoneInputKey((key) => key + 1);
  }, [fieldDigits, country, countryCode]);

  const handleChange = (nextValue, countryData) => {
    isInternalChangeRef.current = true;
    const nextCountry = countryData?.countryCode;
    const nextDialCode = digitsOnly(countryData?.dialCode || selectedDialCode);

    if (nextCountry) {
      setSelectedCountry(String(nextCountry).toLowerCase());
    }
    if (nextDialCode) {
      setSelectedDialCode(nextDialCode);
    }

    if (formValueIsFullInternational) {
      field.setValue(nextValue);
      onChange?.(nextValue, countryData);
      return;
    }

    const normalizedValue = digitsOnly(nextValue);
    const isCountryOnlyValue =
      normalizedValue === nextDialCode ||
      (nextDialCode && normalizedValue.length <= nextDialCode.length);
    if (fieldDigits && isCountryOnlyValue) {
      return;
    }

    const nationalNumber = normalizedValue.slice(nextDialCode.length);

    field.setValue(nationalNumber);
    onChange?.(nationalNumber, countryData);
  };

  return (
    <UIFieldShell
      id={name}
      label={label}
      optionalLabel={optionalLabel}
      required={required}
      helperText={field.helperText}
      error={field.showError}
    >
      <div
        className={clx(
          "ui-phone-field",
          `ui-phone-field--${size}`,
          lang === "ar" ? "ui-phone-field--ar" : "",
          fieldDigits ? "ui-phone-field--filled" : "",
          field.showError ? "ui-phone-field--error" : "",
          disabled ? "ui-phone-field--disabled" : "",
        )}
        style={{ "--phone-iso": `"${selectedCountry.toUpperCase()}"` }}
      >
        <div ref={directionRef} className="ui-phone-field__direction" dir="ltr" onClick={scheduleSyncDropdownPosition}>
          <PhoneInput
            key={`${phoneInputKey}-${lang}`}
            {...props}
            country={selectedCountry}
            value={phoneValue}
            onChange={handleChange}
            onBlur={(event) => {
              field.setTouched(true);
              onBlur?.(event);
            }}
            preferredCountries={preferredCountries}
            disabled={disabled}
            disableDropdown={disableDropdown}
            placeholder={placeholder}
            countryCodeEditable={false}
            enableSearch
            inputProps={{
              id: name,
              name,
              required,
              "aria-invalid": field.showError,
            }}
            containerClass="ui-phone-field__container"
            buttonClass="ui-phone-field__button"
            inputClass="ui-phone-field__input"
            dropdownClass="ui-phone-field__dropdown"
            searchClass="ui-phone-field__search"
            specialLabel=""
            localization={phoneLocalization}
            searchPlaceholder={sharedMessages.searchCountry}
          />
          {!disableDropdown && (
            <span className="ui-phone-field__chevron" aria-hidden="true">
              <ChevronDown
                className="ui-phone-field__chevron-down"
                size={18}
                color="var(--color-foreground-subtle)"
                aria-hidden="true"
              />
              <ChevronUp
                className="ui-phone-field__chevron-up"
                size={18}
                color="var(--color-foreground-subtle)"
                aria-hidden="true"
              />
            </span>
          )}
        </div>
      </div>
    </UIFieldShell>
  );
};

UIPhoneField.propTypes = {
  label: PropTypes.node,
  optionalLabel: PropTypes.node,
  required: PropTypes.bool,
  name: PropTypes.string,
  value: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  country: PropTypes.string,
  countryCode: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(["medium", "large"]),
  preferredCountries: PropTypes.arrayOf(PropTypes.string),
  formValueIsFullInternational: PropTypes.bool,
  disableDropdown: PropTypes.bool,
};

UIPhoneField.defaultProps = {
  label: null,
  optionalLabel: null,
  required: false,
  name: undefined,
  value: undefined,
  error: undefined,
  helperText: null,
  onChange: undefined,
  onBlur: undefined,
  country: "sa",
  countryCode: "966",
  placeholder: "",
  disabled: false,
  size: "medium",
  preferredCountries: ["sa"],
  formValueIsFullInternational: false,
  disableDropdown: false,
};

export default UIPhoneField;
